import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Reveal, SectionLabel, MaskedHeading } from "@/components/site/Motion";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1543845805-25c2940b5202?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export const About = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" className="py-24 md:py-36 px-6 md:px-12 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left: image */}
        <div ref={ref} className="lg:col-span-5 relative">
          <div className="relative overflow-hidden">
            <motion.img
              style={{ y, scale: 1.15 }}
              src={ABOUT_IMG}
              alt="VIP1 ambiance"
              className="w-full h-[420px] md:h-[560px] object-cover"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 to-transparent" />
          </div>
          <div className="absolute -bottom-6 -right-4 md:-right-8 bg-brand text-white p-6 md:p-8">
            <p className="font-display text-3xl md:text-4xl font-700 leading-none">10+</p>
            <p className="font-body text-xs tracking-widest uppercase mt-2 text-white/90">
              {lang === "en" ? "Years" : "Yıl"}
            </p>
          </div>
        </div>

        {/* Right: content */}
        <div className="lg:col-span-7 lg:pl-8">
          <SectionLabel>{t.about.overline}</SectionLabel>
          <h2 className="font-heading text-4xl md:text-6xl leading-[1.02] text-white tracking-tight">
            <MaskedHeading text={t.about.title} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 font-body text-base md:text-lg text-white/60 max-w-2xl leading-relaxed">
              {t.about.desc}
            </p>
          </Reveal>

          <div className="mt-12 space-y-px">
            {t.about.chapters.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.1}>
                <div className="group flex gap-6 md:gap-10 py-7 border-t border-white/10 hover:border-brand/40 transition-colors">
                  <span className="font-display text-brand text-lg md:text-xl pt-1">{c.no}</span>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl text-white group-hover:translate-x-1 transition-transform">
                      {c.title}
                    </h3>
                    <p className="mt-2 font-body text-white/55 leading-relaxed max-w-xl">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
